import type { MissingList } from './types/missing-list.type';

/**
 * Derived display status for a {@link MissingList} row.
 */
export type MissingListStatus = 'error' | 'good' | 'missing' | 'pending';

/**
 * Classifies a {@link MissingList} row into its single display status.
 *
 * @remarks
 * Single source of truth shared by the display modes (Table, Leaderboard,
 * Cards) so classification can never diverge. `Missing` is the **absence of
 * status** — a row is Missing when none of `isError` / `isComplete` /
 * `isPending` is set (the SP could not determine a status). The three real
 * flags are mutually exclusive; precedence only guards an unexpected collision.
 *
 * @param row - The missing-list row to classify.
 * @returns The derived `'error' | 'good' | 'missing' | 'pending'` status.
 */
export const deriveMissingListStatus = (row: MissingList): MissingListStatus => {
  if (row.isError) {
    return 'error';
  }

  if (row.isComplete) {
    return 'good';
  }

  if (row.isPending) {
    return 'pending';
  }

  return 'missing';
};
